import { Add, ArrowBack, Search } from '@mui/icons-material'
import {
  Box,
  Hidden,
  IconButton,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'
import React from 'react'
import { connect } from 'react-redux'
import UserCard from 'components/admin/user-admin-user-card'
import UserAdminUserEditModal from 'components/admin/user-admin-user-edit-modal'
import {
  Operations as LayoutOperations,
  operations as layoutOperations
} from 'states/dux/layout/operations'
import { Payload as LayoutPayload } from 'states/dux/layout/types'
import {
  Operations as ThemeOperations,
  operations as themeOperations
} from 'states/dux/theme/operations'
import { Payload as ThemePayload } from 'states/dux/theme/types'
import {
  Operations as UserAdminOperations,
  operations as userAdminOperations
} from 'states/dux/user-admin/operations'
import { Payload as UserAdminPayload } from 'states/dux/user-admin/types'
import {
  Operations as UserOperations,
  operations as userOperations
} from 'states/dux/user/operations'
import { Payload as UserPayload } from 'states/dux/user/types'
import State from 'states/types'
import { NivosUser } from 'types/user'
import {
  get,
  mapOperationsToDispatchProps,
  objectifyDateStringFields
} from 'utils/common'

interface StateProps {
  theme: ThemePayload
  layout: LayoutPayload
  user: UserPayload
  userAdmin: UserAdminPayload
}

interface DispatchProps {
  operations: {
    theme: ThemeOperations
    layout: LayoutOperations
    user: UserOperations
    userAdmin: UserAdminOperations
  }
}

interface OwnProps {
  onCloseContent: () => void
}

type AppProps = StateProps & DispatchProps & OwnProps

const UserAdminFC: React.FC<any> = (props: AppProps) => {
  const { userAdmin, theme, onCloseContent, operations } = props
  const [selectedUser, setSelectedUser] = React.useState<NivosUser>()
  const [searchKey, setSearchKey] = React.useState('')
  const [filtered, setFiltered] = React.useState(false)
  const isMobileView = useMediaQuery('(max-width:600px)')

  const _filterData = (): void => {
    void get(`/account/?search=${searchKey}`).then(data => {
      try {
        operations.userAdmin.set(
          data.map((_userData: NivosUser) =>
            objectifyDateStringFields(_userData)
          )
        )
        setFiltered(searchKey.trim() !== '')
      } catch (e) {
        console.log(e)
      }
    })
  }

  const emptyUser: NivosUser = {
    id: 0,
    firstName: '',
    lastName: '',
    fullName: '',
    groups: [],
    contactList: [],
    email: '',
    address: '',
    authenticated: false,
    image: undefined,
    accessToken: undefined,
    refreshToken: undefined,
    loading: false
  }

  React.useEffect(() => {
    if (userAdmin.data.length === 0) {
      void get('/account/').then(data => {
        try {
          operations.userAdmin.set(
            data.map((_userData: NivosUser) =>
              objectifyDateStringFields(_userData)
            )
          )
        } catch (e) {
          console.log(e)
        }
      })
    }
  }, [])

  return (
    <Box
      border={1}
      borderRadius={'12px'}
      borderColor={theme.secondaryColor}
      padding={isMobileView ? 1 : 2}
      width={0.9}
      minHeight={isMobileView ? '95vh' : '85vh'}
      marginX={'auto'}
    >
      <Box display={'flex'} alignItems={'center'}>
        <IconButton
          size={'medium'}
          onClick={() => {
            // Fetch new if current results are filtered
            if (filtered) {
              void get('/account/').then(data => {
                try {
                  operations.userAdmin.set(
                    data.map((_userData: NivosUser) =>
                      objectifyDateStringFields(_userData)
                    )
                  )
                } catch (e) {
                  console.log(e)
                }
              })
            }
            onCloseContent()
          }}
        >
          <ArrowBack fontSize={'large'} />
        </IconButton>
        <Box
          paddingLeft={1}
          borderRight={1}
          height={'56px'}
          color={theme.secondaryColor}
        />
        <Box
          paddingRight={2}
          borderLeft={1}
          height={'56px'}
          color={theme.secondaryColor}
        />
        <Hidden smDown>
          <Typography variant={'h3'}>User Admin</Typography>
        </Hidden>
        <Box flexGrow={1} />
        <Box>
          <TextField
            id={'search-user-name'}
            type={'text'}
            placeholder={'User name...'}
            value={searchKey}
            onChange={event => setSearchKey(event.currentTarget.value)}
            onKeyDown={event => {
              if (event.code === 'Enter') {
                _filterData()
              }
            }}
            InputProps={{
              startAdornment: <Search />
            }}
          />
        </Box>
        <Tooltip title={'Register new user'} placement={'top'}>
          <IconButton
            sx={{ margin: 1 }}
            onClick={() => setSelectedUser(emptyUser)}
          >
            <Add fontSize={'large'} />
          </IconButton>
        </Tooltip>
      </Box>
      <Box padding={isMobileView ? 1 : 4}>
        <UserAdminUserEditModal
          selectedUser={selectedUser}
          setSelectedUser={setSelectedUser}
        />
        <Box
          display={'flex'}
          flexWrap={'wrap'}
          justifyContent={isMobileView ? 'center' : 'flex-start'}
        >
          {userAdmin.data.map((item, index) => (
            <UserCard
              key={index}
              userDetails={item}
              onClickEvent={userDetail => setSelectedUser(userDetail)}
            />
          ))}
          {userAdmin.data.length === 0 && (
            <Typography variant="h3" align="center" marginY={2}>
              No Data
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  )
}

const mapStateToProps = (state: State): StateProps => {
  return {
    theme: state.theme,
    layout: state.layout,
    user: state.user,
    userAdmin: state.userAdmin
  }
}

const mapDispatchToProps = mapOperationsToDispatchProps({
  theme: themeOperations,
  layout: layoutOperations,
  user: userOperations,
  userAdmin: userAdminOperations
})

const UserAdmin = connect(
  mapStateToProps,
  mapDispatchToProps
)(UserAdminFC) as React.FC<OwnProps>

export default UserAdmin
